import React, { useCallback, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, AlertTriangleIcon, CheckIcon, FacebookIcon, InstagramIcon, TwitterIcon, UploadCloudIcon, FileTextIcon, LayoutTemplateIcon, PaletteIcon, Share2Icon, TagsIcon, UsersIcon } from 'lucide-react';
import { InternalTeamsConfig } from './notice/InternalTeamsConfig';
import { useLanguage } from '../contexts/LanguageContext';
import type { ChannelKey, Localized } from '../types/notice';
import { CHANNEL_ICONS, CHANNEL_LABELS } from '../data/channels';
import { IconTooltip } from './IconTooltip';
import { CategoryFormDialog } from './notice/CategoryFormDialog';
import { ConfirmDeleteDialog } from './notice/ConfirmDeleteDialog';
import { CATEGORIES, getCategoryIcon, getCategoryLabel, getTemplateCategory, getTemplateChannels, type NoticeTemplate } from '../data/communications';
export type ConfigSection = 'categories' | 'equipes' | 'reseaux' | 'modeles' | 'identite';
type Section = ConfigSection;
export interface Categorie {
  id: string;
  titre: Localized;
  description: Localized;
  urgence: boolean;
  defaut: boolean;
  statut: 'active' | 'brouillon' | 'inactive';
}
export const MOCK_CATEGORIES: Categorie[] = [{
  id: '1',
  titre: {
    fr: 'Urgence',
    en: 'Emergency'
  },
  description: {
    fr: 'Avis critiques nécessitant une action immédiate',
    en: 'Critical notices requiring immediate action'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '2',
  titre: {
    fr: 'Eau potable',
    en: 'Drinking water'
  },
  description: {
    fr: "Avis d'ébullition et coupures d'eau",
    en: 'Boil advisories and water shutdowns'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '3',
  titre: {
    fr: 'Travaux',
    en: 'Public works'
  },
  description: {
    fr: 'Fermetures de rues et chantiers',
    en: 'Street closures and work sites'
  },
  urgence: false,
  defaut: false,
  statut: 'active'
}, {
  id: '4',
  titre: {
    fr: 'Information générale',
    en: 'General information'
  },
  description: {
    fr: 'Nouvelles et mises à jour de la ville',
    en: 'City news and updates'
  },
  urgence: false,
  defaut: true,
  statut: 'active'
}];
interface CommunicationsConfigurerProps {
  categories: Categorie[];
  onCategoriesChange: (categories: Categorie[]) => void;
  templates: NoticeTemplate[];
  onCreateTemplate: () => void;
  onEditTemplate: (template: NoticeTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  /** Section shown first — e.g. back on "Modèles" after editing a template. */
  initialSection?: Section;
}
export function CommunicationsConfigurer({
  categories,
  onCategoriesChange,
  templates,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  initialSection = 'categories'
}: CommunicationsConfigurerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [activeSection, setActiveSection] = useState<Section>(initialSection);
  /** Template awaiting a delete confirmation, shown inline on its row. */
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  /** Category form: closed, or open on a category (null = creating). */
  const [categoryForm, setCategoryForm] = useState<{
    open: false;
  } | {
    open: true;
    category: Categorie | null;
  }>({
    open: false
  });
  const [categoryToDelete, setCategoryToDelete] = useState<Categorie | null>(null);
  const closeCategoryForm = useCallback(() => setCategoryForm({
    open: false
  }), []);
  const cancelCategoryDelete = useCallback(() => setCategoryToDelete(null), []);
  const saveCategory = (saved: Categorie) => {
    const exists = categories.some(c => c.id === saved.id);
    const next = exists ? categories.map(c => c.id === saved.id ? saved : c) : [...categories, saved];
    // Only one category can be the default one.
    onCategoriesChange(saved.defaut ? next.map(c => c.id === saved.id ? c : {
      ...c,
      defaut: false
    }) : next);
    setCategoryForm({
      open: false
    });
  };
  const confirmCategoryDelete = () => {
    if (!categoryToDelete) return;
    onCategoriesChange(categories.filter(c => c.id !== categoryToDelete.id));
    setCategoryToDelete(null);
  };
  const statutLabel = (statut: Categorie['statut']) => statut === 'active' ? t('Active', 'Active') : statut === 'brouillon' ? t('Brouillon', 'Draft') : t('Inactive', 'Inactive');
  const renderCategories = () => <div className="space-y-6" data-id="element-1658" data-mp="yjhtpndyy53x">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1659" data-mp="yipxqbggm5d4">
        <div data-id="element-1660" data-mp="jkxizliqlkzz">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1661" data-mp="jlfmn37qb1hx">
            {t("Catégories d'avis", 'Notice categories')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1662" data-mp="omva11xdpk3h">
            {t("Gérez les catégories disponibles lors de la création d'un avis.", 'Manage the categories available when creating a notice.')}
          </p>
        </div>
        <button type="button" onClick={() => setCategoryForm({
        open: true,
        category: null
      })} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1663" data-mp="1rc33yxm7tutv">
          <PlusIcon className="w-4 h-4" data-id="element-1664" data-mp="1wchfipn0zmwn" /> {t('Nouvelle catégorie', 'New category')}
        </button>
      </div>

      {categories.length === 0 ? <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-id="element-1665" data-mp="18l2agp3v6rhp">
          <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-[#4F46E5] mb-3" data-id="element-1666" data-mp="13lz513jz2j97">
            <TagsIcon className="w-6 h-6" data-id="element-1667" data-mp="1cp06gqxw7m6j" />
          </div>
          <p className="font-semibold text-slate-900" data-id="element-1668" data-mp="1cngsj6zpln7x">
            {t('Aucune catégorie', 'No categories')}
          </p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1669" data-mp="owt5tfl4wt1b">
            {t('Créez une première catégorie pour classer vos avis.', 'Create a first category to organize your notices.')}
          </p>
        </div> : <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-1670" data-mp="2p041e1mpp0g8">
        <div className="overflow-x-auto" data-id="element-1671" data-mp="2ttgfvixofqv9">
          <table className="w-full text-left border-collapse" data-id="element-1672" data-mp="2tuht9u260pg5">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1673" data-mp="2yqoytlqnfyxk">
              <tr data-id="element-1674" data-mp="2149i4m61r5b2">
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1675" data-mp="214rle2r4mjsh">
                  {t('Titre et description', 'Title & description')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1676" data-mp="2b0mddoh8pr7x">
                  {t('Urgence', 'Emergency')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1677" data-mp="3mhm106sn7hvm">
                  {t('Par défaut', 'Default')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1678" data-mp="1fsai2b72hdik">
                  {t('Statut', 'Status')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right" data-id="element-1679" data-mp="1avk3xtetorzs">
                  {t('Actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1680" data-mp="w4l2d2cen3pp">
              {categories.map(cat => <tr key={cat.id} onDoubleClick={() => setCategoryForm({
              open: true,
              category: cat
            })} className="hover:bg-slate-50 transition-colors" data-id="element-1681" data-mp="s2zhatppvj4f">
                  <td className="px-4 py-3" data-id="element-1682" data-mp="i5lbrp5c0eqw">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-1683" data-mp="i6mr45tdrnef">
                      {cat.titre[lang]}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-1684" data-mp="2cib6g93ixrzc">
                      {cat.description[lang]}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1685" data-mp="1lfjwyevwwcsn">
                    {cat.urgence && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-red-100 text-red-600" data-id="element-1686" data-mp="1qemqp36fywlb">
                        <AlertTriangleIcon className="w-3 h-3" data-id="element-1687" data-mp="1gmqqnmksoweg" />
                      </span>}
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1688" data-mp="12lxukxeuht98">
                    {cat.defaut && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-emerald-100 text-emerald-600" data-id="element-1689" data-mp="17n3pn1vz9slx">
                        <CheckIcon className="w-3 h-3" data-id="element-1690" data-mp="sqmeobrpafzw" />
                      </span>}
                  </td>
                  <td className="px-4 py-3" data-id="element-1691" data-mp="2n2t2qb5p0r9m">
                    <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border ${cat.statut === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : cat.statut === 'brouillon' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-1692" data-mp="2n3vtmj30mtpj">
                      {statutLabel(cat.statut)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1693" data-mp="ld50ru2z9sn7">
                    <div className="flex items-center justify-end gap-2" data-id="element-1694" data-mp="lbk6m5kksbpv">
                      <button type="button" onClick={() => setCategoryForm({
                    open: true,
                    category: cat
                  })} className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" aria-label={`${t('Modifier', 'Edit')} ${cat.titre[lang]}`} data-id="element-1695" data-mp="z3qqo2qun2ul">
                        <PencilIcon className="w-4 h-4" data-id="element-1696" data-mp="3avnjksrbsiqx" />
                      </button>
                      <button type="button" onClick={() => setCategoryToDelete(cat)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label={`${t('Supprimer', 'Delete')} ${cat.titre[lang]}`} data-id="element-1697" data-mp="35wazugvr3rsk">
                        <TrashIcon className="w-4 h-4" data-id="element-1698" data-mp="zs7nd2lu8sxe" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>}

      <CategoryFormDialog open={categoryForm.open} category={categoryForm.open ? categoryForm.category : null} onClose={closeCategoryForm} onSave={saveCategory} data-id="element-1699" data-mp="1nfpd6sljlgxq" />

      <ConfirmDeleteDialog open={Boolean(categoryToDelete)} title={t('Supprimer cette catégorie ?', 'Delete this category?')} message={categoryToDelete && <>
              {t('La catégorie ', 'The category ')}
              <span className="font-semibold text-slate-700" data-id="element-1701" data-mp="3p1l1jg8wor0b">
                « {categoryToDelete.titre[lang]} »
              </span>
              {t(' ne sera plus proposée à la création d’un avis. Les avis déjà envoyés ne sont pas modifiés.', ' will no longer be offered when creating a notice. Notices already sent are not affected.')}
            </>} onCancel={cancelCategoryDelete} onConfirm={confirmCategoryDelete} data-id="element-1700" data-mp="1emm1opugje5q" />
    </div>;
  const renderReseaux = () => <div className="space-y-6" data-id="element-1702" data-mp="gsl2vdktqzsn">
      <div data-id="element-1703" data-mp="lkunqf7319o2">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1704" data-mp="ll4gubnfbwxz">
          {t('Réseaux sociaux', 'Social media')}
        </h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-1705" data-mp="pmppfksxdp2d">
          {t('Connectez les comptes officiels pour la publication automatisée.', 'Connect the official accounts for automated publishing.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1706" data-mp="ziavbu1479he">
        {/* Facebook */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1707" data-mp="2vo1vvdqrkdbr">
          <div className="flex items-start justify-between mb-4" data-id="element-1708" data-mp="2qqibrx7dx6bw">
            <div className="flex items-center gap-3" data-id="element-1709" data-mp="35emxla1qhxyw">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-1710" data-mp="30mv70zikvs8r">
                <FacebookIcon className="w-5 h-5" data-id="element-1711" data-mp="3ee02dpewxyda" />
              </div>
              <div data-id="element-1712" data-mp="3eg4darb8bxn2">
                <h4 className="font-semibold text-slate-900" data-id="element-1713" data-mp="3acy9dqblaijr">Facebook</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1714" data-mp="3aer5wcshnme1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1715" data-mp="3oad0czhhqydu" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1716" data-mp="1tyok8tziwprv">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1717" data-mp="4ilztgrhu19b">
            <span className="font-medium text-slate-900" data-id="element-1718" data-mp="9gnl88ce6cny">
              {t('Page :', 'Page:')}
            </span>{' '}
            Ville de Windsor
          </div>
        </div>

        {/* X (Twitter) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1719" data-mp="3vux647h452jz">
          <div className="flex items-start justify-between mb-4" data-id="element-1720" data-mp="3hv5u2ca5pw8m">
            <div className="flex items-center gap-3" data-id="element-1721" data-mp="3mv0namhemxwu">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-900" data-id="element-1722" data-mp="3mwv5vu57edxr">
                <TwitterIcon className="w-5 h-5" data-id="element-1723" data-mp="380497smwzgbu" />
              </div>
              <div data-id="element-1724" data-mp="3cxvysg036qcv">
                <h4 className="font-semibold text-slate-900" data-id="element-1725" data-mp="3cxco9kcrn48v">X (Twitter)</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1726" data-mp="167y46lghth6m">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1727" data-mp="1b4g3v6wyupc3" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1728" data-mp="338c5ttw2jtwd">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1729" data-mp="2t9mk1q11xpau">
            <span className="font-medium text-slate-900" data-id="element-1730" data-mp="2oa09xpgisp69">
              {t('Compte :', 'Account:')}
            </span>{' '}
            @villewindsor
          </div>
        </div>

        {/* Instagram */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1731" data-mp="2fhgesyzft2q6">
          <div className="flex items-start justify-between mb-4" data-id="element-1732" data-mp="2aivgj53qwt4d">
            <div className="flex items-center gap-3" data-id="element-1733" data-mp="2agio1y0d30d2">
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-600" data-id="element-1734" data-mp="25jsbhknrmmwe">
                <InstagramIcon className="w-5 h-5" data-id="element-1735" data-mp="20lqqgu6s4dia" />
              </div>
              <div data-id="element-1736" data-mp="1qh0kmd898p8s">
                <h4 className="font-semibold text-slate-900" data-id="element-1737" data-mp="1qfh5wr28ym94">Instagram</h4>
                <p className="text-xs text-slate-400 font-medium flex items-center gap-1 mt-0.5" data-id="element-1738" data-mp="1vf2d1orkm0wx">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-1739" data-mp="20e5aan1b72uc" />{' '}
                  {t('Non connecté', 'Not connected')}
                </p>
              </div>
            </div>
          </div>
          <button className="mt-auto w-full py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1740" data-mp="17qsl053rkgxc">
            {t('Connecter le compte', 'Connect the account')}
          </button>
        </div>
      </div>
    </div>;
  const renderModeles = () => <div className="space-y-6" data-id="element-1741" data-mp="1mf5k3ay5efw2">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1742" data-mp="1mgglx5819dkj">
        <div data-id="element-1743" data-mp="oygldq4hvtqw">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1744" data-mp="oyqdpkd0iczr">
            {t("Modèles d'avis", 'Notice templates')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1745" data-mp="twi2w6m9vl09">
            {t("Configurez des modèles prédéfinis pour accélérer la création d'avis.", 'Set up predefined templates to speed up notice creation.')}
          </p>
        </div>
        <button type="button" onClick={onCreateTemplate} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1746" data-mp="vmexxq5rdhmj">
          <PlusIcon className="w-4 h-4" data-id="element-1747" data-mp="30vpthggcybs" /> {t('Nouveau modèle', 'New template')}
        </button>
      </div>

      {templates.length === 0 ? <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-id="element-1748" data-mp="cy9vqn4r24pu">
          <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-[#4F46E5] mb-3" data-id="element-1749" data-mp="7x40ahjpg3cm">
            <LayoutTemplateIcon className="w-6 h-6" data-id="element-1750" data-mp="1oaua84xi9lu6" />
          </div>
          <p className="font-semibold text-slate-900" data-id="element-1751" data-mp="1je3vbkxfswac">
            {t('Aucun modèle', 'No templates')}
          </p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1752" data-mp="1qw20xechzn8a">
            {t('Créez un modèle ici ou enregistrez un avis comme modèle depuis le formulaire.', 'Create a template here, or save a notice as a template from the composer.')}
          </p>
        </div> : <ul className="grid grid-cols-1 gap-3" data-id="element-1753" data-mp="1ly8r8bbzkb39">
          {templates.map(tpl => {
        const confirming = confirmDeleteId === tpl.id;
        const categoryId = getTemplateCategory(tpl);
        const CategoryIcon = getCategoryIcon(categoryId);
        const urgent = Boolean(CATEGORIES.find(c => c.value === categoryId)?.urgent);
        const channels = getTemplateChannels(tpl) as ChannelKey[];
        return <li key={tpl.id} onClick={() => !confirming && onEditTemplate(tpl)} className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center justify-between gap-4 hover:border-[#4F46E5]/50 hover:shadow-sm transition-all group cursor-pointer" data-id="element-1754" data-mp="2sl4bekdc0bum">
                <div className="flex items-center gap-4 min-w-0" data-id="element-1755" data-mp="23u86hgrkmtx3">
                  <IconTooltip label={getCategoryLabel(categoryId)[lang]} data-id="element-1756" data-mp="1yxhp8q415sa3">
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${urgent ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-[#4F46E5]'}`} data-id="element-1757" data-mp="1txvdym7rim3x">
                      <CategoryIcon className="w-5 h-5" aria-hidden="true" data-id="element-1758" data-mp="2mk6ddtjmugz5" />
                    </span>
                  </IconTooltip>
                  {/* Channels: up to 3 per row, 2 rows, vertically centered */}
                  <div className="grid grid-cols-3 gap-1 w-[68px] flex-shrink-0 content-center" data-id="element-1759" data-mp="167r785za268p">
                    {channels.map(c => {
                const Icon = CHANNEL_ICONS[c];
                return <IconTooltip key={c} label={CHANNEL_LABELS[c][lang]} data-id="element-1760" data-mp="3cwt06dzt63g2">
                          <span className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-[#4F46E5] transition-colors" data-id="element-1761" data-mp="3huur1l3h7723">
                            <Icon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1762" data-mp="3mtfkn1o82shk" />
                          </span>
                        </IconTooltip>;
              })}
                  </div>
                  <div className="min-w-0" data-id="element-1763" data-mp="ecygh3ba5as">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-1764" data-mp="4foq0s5qx0lm">
                      <h4 className="font-semibold text-slate-900 truncate" data-id="element-1765" data-mp="4i2j4vgv82qj">
                        {tpl.name[lang]}
                      </h4>
                      {tpl.custom && <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#4F46E5] bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-1766" data-mp="ecba3eq8znml">
                          {t('Équipe', 'Team')}
                        </span>}
                    </div>
                    <p className="text-sm text-slate-500 truncate mt-0.5" data-id="element-1767" data-mp="o5zmiudbiuzx">
                      {tpl.description[lang]}
                    </p>
                  </div>
                </div>

                {confirming ? <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()} data-id="element-1768" data-mp="x39vua6ksa27">
                    <span className="text-sm text-slate-600 hidden sm:inline" data-id="element-1769" data-mp="16ue9n6kilovg">
                      {t('Supprimer ce modèle ?', 'Delete this template?')}
                    </span>
                    <button type="button" onClick={() => setConfirmDeleteId(null)} className="px-3 py-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50" data-id="element-1770" data-mp="1gu581jc74o0t">
                      {t('Annuler', 'Cancel')}
                    </button>
                    <button type="button" onClick={() => {
              onDeleteTemplate(tpl.id);
              setConfirmDeleteId(null);
            }} className="px-3 py-1.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-1771" data-mp="3f2j4veii8zgc">
                      {t('Supprimer', 'Delete')}
                    </button>
                  </div> : <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-1772" data-mp="1tn3dfxgpmup">
                    <button type="button" aria-label={t('Modifier', 'Edit')} onClick={e => {
              e.stopPropagation();
              onEditTemplate(tpl);
            }} className="p-2 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-1773" data-mp="3oze27u31ktt1">
                      <PencilIcon className="w-4 h-4" data-id="element-1774" data-mp="2a4fi3fvj48qq" />
                    </button>
                    <button type="button" aria-label={t('Supprimer', 'Delete')} onClick={e => {
              e.stopPropagation();
              setConfirmDeleteId(tpl.id);
            }} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1775" data-mp="2f1ede1qxilqi">
                      <TrashIcon className="w-4 h-4" data-id="element-1776" data-mp="1w5xynucnpiem" />
                    </button>
                  </div>}
              </li>;
      })}
        </ul>}
    </div>;
  const renderIdentite = () => <div className="space-y-6" data-id="element-1777" data-mp="18eistrlo8iym">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1778" data-mp="18fbrs6ee2467">
        <div data-id="element-1779" data-mp="1ddvei90t3jzm">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1780" data-mp="1ddn3yns5oqrl">
            {t('Identité graphique', 'Visual identity')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1781" data-mp="trtvnwew6hve">
            {t("Personnalisez l'apparence des communications envoyées aux citoyens.", 'Customize how the communications sent to citizens look.')}
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-1782" data-mp="ypll8js2drwf">
          {t('Enregistrer les modifications', 'Save changes')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-8 shadow-sm" data-id="element-1783" data-mp="17qthkwb0sinv">
        {/* Logo */}
        <div data-id="element-1784" data-mp="1mibuumchvz3j">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1785" data-mp="1mi2775exa410">
            {t('Logo officiel', 'Official logo')}
          </label>
          <div className="flex items-center gap-6 flex-wrap" data-id="element-1786" data-mp="1vhywn994z6x4">
            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center bg-slate-50 text-slate-400 hover:border-[#4F46E5] hover:bg-indigo-50 transition-colors cursor-pointer" data-id="element-1787" data-mp="1qhudfbgufy37">
              <UploadCloudIcon className="w-6 h-6 mb-2" data-id="element-1788" data-mp="3pw468j53jwc5" />
              <span className="text-xs font-medium" data-id="element-1789" data-mp="3unuhqsfrsq55">
                {t('Changer le logo', 'Change logo')}
              </span>
            </div>
            <div className="flex-1 min-w-[15rem]" data-id="element-1790" data-mp="2is0z1y387nn">
              <p className="text-sm text-slate-500 mb-2" data-id="element-1791" data-mp="31z6tqp3mzmnw">
                {t("Ce logo sera utilisé dans l'en-tête des courriels et sur le portail citoyen.", 'This logo is used in the email header and on the citizen portal.')}
              </p>
              <p className="text-xs text-slate-400" data-id="element-1792" data-mp="3fyyf5awhg5bx">
                {t('Format recommandé : PNG transparent, 400x100px max, 2 Mo max.', 'Recommended format: transparent PNG, 400x100px max, 2 MB max.')}
              </p>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" data-id="element-1793" data-mp="15d787xd8hxza" />

        {/* Colours */}
        <div data-id="element-1794" data-mp="15eibzx6oj5qe">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1795" data-mp="10duohlhnt70x">
            {t('Couleurs de la marque', 'Brand colours')}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1796" data-mp="vlbawoiz2bw4">
            <div data-id="element-1797" data-mp="rm2flf8aa5yg">
              <label className="block text-xs text-slate-500 mb-1.5" data-id="element-1798" data-mp="rkhnrxdct14a">
                {t('Couleur principale', 'Primary colour')}
              </label>
              <div className="flex items-center gap-3" data-id="element-1799" data-mp="hn39a3azqmem">
                <div className="w-10 h-10 rounded-lg bg-[#4F46E5] border border-slate-200 shadow-sm" data-id="element-1800" data-mp="hlidkacxwxf6" />
                <input type="text" defaultValue="#4F46E5" aria-label={t('Couleur principale', 'Primary colour')} className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-1801" data-mp="coa3kx1y3zp0" />
              </div>
            </div>
            <div data-id="element-1802" data-mp="d8zch393940">
              <label className="block text-xs text-slate-500 mb-1.5" data-id="element-1803" data-mp="cpspwj1w75a">
                {t('Couleur secondaire', 'Secondary colour')}
              </label>
              <div className="flex items-center gap-3" data-id="element-1804" data-mp="56bugtnoeqmb">
                <div className="w-10 h-10 rounded-lg bg-[#0F172A] border border-slate-200 shadow-sm" data-id="element-1805" data-mp="3njrt03uls9rk" />
                <input type="text" defaultValue="#0F172A" aria-label={t('Couleur secondaire', 'Secondary colour')} className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-1806" data-mp="3rklj93viq0nm" />
              </div>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" data-id="element-1807" data-mp="f1m1xwwufq7h" />

        {/* Email footer */}
        <div data-id="element-1808" data-mp="2ut9uzh8qkyl0">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1809" data-mp="2utjk72lyj1pn">
            {t('Pied de page des courriels', 'Email footer')}
          </label>
          <textarea rows={4} aria-label={t('Pied de page des courriels', 'Email footer')} defaultValue={'Ville de Windsor\n11, rue St-Georges, Windsor (Québec) J1S 1J4\nTéléphone : 819 845-7888'} className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#4F46E5] resize-none" data-id="element-1810" data-mp="2zqjigrckf067" />
          <p className="text-xs text-slate-500 mt-2" data-id="element-1811" data-mp="28ldxawsmkxsl">
            {t('Ce texte apparaîtra au bas de toutes les communications par courriel.', 'This text appears at the bottom of every email communication.')}
          </p>
        </div>
      </div>
    </div>;
  const nav: {
    id: Section;
    label: string;
    icon: typeof TagsIcon;
  }[] = [{
    id: 'categories',
    label: t('Catégories', 'Categories'),
    icon: TagsIcon
  }, {
    id: 'modeles',
    label: t('Modèles', 'Templates'),
    icon: LayoutTemplateIcon
  }, {
    id: 'equipes',
    label: t('Équipes internes', 'Internal teams'),
    icon: UsersIcon
  }, {
    id: 'reseaux',
    label: t('Réseaux sociaux', 'Social media'),
    icon: Share2Icon
  }, {
    id: 'identite',
    label: t('Identité', 'Identity'),
    icon: PaletteIcon
  }];
  return <div className="flex flex-col lg:flex-row gap-8 font-['Inter']" data-id="element-1812" data-mp="19axucyjp5i9u">
      {/* Section switcher */}
      <div className="lg:w-64 flex-shrink-0" data-id="element-1813" data-mp="1e9hjf7tl7a6f">
        <nav aria-label={t('Sections de configuration', 'Configuration sections')} className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4 shadow-sm" data-id="element-1814" data-mp="1ebc4cm4uz2ai">
          {nav.map(item => {
          const Icon = item.icon;
          const active = activeSection === item.id;
          return <button key={item.id} onClick={() => setActiveSection(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${active ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1815" data-mp="1x0s59akggwrg">
                <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-slate-400'}`} data-id="element-1816" data-mp="3mgl031bcrg4u" />
                {item.label}
              </button>;
        })}
        </nav>
      </div>

      {/* Content area */}
      <div className="flex-1 min-w-0" data-id="element-1817" data-mp="e1krhqrpv07i">
        {activeSection === 'categories' && renderCategories()}
        {activeSection === 'equipes' && <InternalTeamsConfig data-id="element-1818" data-mp="93iz2f8ehojd" />}
        {activeSection === 'reseaux' && renderReseaux()}
        {activeSection === 'modeles' && renderModeles()}
        {activeSection === 'identite' && renderIdentite()}
      </div>
    </div>;
}